import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { CaretDownIcon } from '@phosphor-icons/react/dist/csr/CaretDown';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';

import { Field, type FieldWidth } from '../Field/Field';
import styles from './Combobox.module.css';

/**
 * Combobox — a filtering single-select for lists too long to scan.
 *
 * Handoff, Forms 5: "Any select over ~8 options is a combobox with type-ahead.
 * Dispensary lists are long."
 *
 * This is the ARIA 1.2 combobox-with-listbox-popup pattern, and the part of it
 * that is easy to get wrong is where focus lives: **DOM focus never leaves the
 * input.** The active option is named by `aria-activedescendant` instead. That
 * is what keeps the user's typing going to the filter while the arrow keys walk
 * the list, and it is why the options carry no `tabIndex` and why selecting
 * with the mouse has to `preventDefault()` on mousedown — a real focus move to
 * a `<li>` would blur the input and close the list before the click landed.
 *
 * Escape both `preventDefault()`s and `stopPropagation()`s while the list is
 * open. The handoff's keyboard contract gives Escape to "close dialog or
 * panel"; a combobox inside a dialog must eat the first Escape or dismissing
 * the menu also dismisses the form around it.
 *
 * **It is also a dropdown.** A click on the field opens the full list, exactly
 * as a native select would, and a second click closes it — typing is a way to
 * narrow the list, never the only way to see it. The caret at the end of the
 * field is the visual cue for that, and is `pointer-events: none` so the click
 * lands on the input underneath it (a separate clickable caret would be a
 * second control the keyboard cannot reach).
 *
 * Everything the component says out loud is a caller string. There are no
 * built-in words — not even "No matches" — because that would ship English to
 * an Arabic agent.
 *
 * **Async mode (`onQueryChange`).** A list too long to send to the browser —
 * the staff directory — is filtered by the SERVER, so the caller owns the
 * options and this component stops filtering them. It reports the filter text
 * instead: `''` whenever the list opens (so the caller can load the first
 * page), then every keystroke (the caller debounces). Two props exist only
 * because the options now change under the selection: `selectedLabel` names
 * the chosen value when the current page no longer contains it, and
 * `loading`/`loadingMessage` replace the empty message while a page is in
 * flight, so "no matches" is never claimed before the answer arrives.
 * Everything else — focus, keys, ARIA — is the same contract, deliberately:
 * a second combobox for the async case would be a second keyboard contract to
 * keep correct.
 */

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  label: string;
  width: FieldWidth;
  options: readonly ComboboxOption[];
  /** The selected option's value, or null for "nothing chosen yet". */
  value: string | null;
  onChange: (value: string) => void;
  /** Shown when the filter matches nothing. Caller-translated. */
  emptyMessage: string;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  /** Called on blur with the committed value. Never called while typing. */
  onValidate?: (value: string | null) => void;
  /**
   * ASYNC MODE (see the docblock): when present, `options` are shown as given,
   * unfiltered, and this receives `''` on open and the typed text on every
   * change.
   */
  onQueryChange?: (query: string) => void;
  /** The label for `value` when `options` does not contain it (async mode). */
  selectedLabel?: string;
  /** A page of options is in flight; `loadingMessage` replaces `emptyMessage`. */
  loading?: boolean;
  loadingMessage?: string;
  id?: string;
  className?: string;
}

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  {
    label,
    width,
    options,
    value,
    onChange,
    emptyMessage,
    placeholder,
    error,
    hint,
    required = false,
    disabled = false,
    onValidate,
    onQueryChange,
    selectedLabel,
    loading = false,
    loadingMessage,
    id,
    className,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((option) => option.value === value) ?? null;
  // What the closed field reads. In async mode the chosen option can fall off
  // the current page; `selectedLabel` keeps it named rather than blank.
  const selectedText = selected?.label ?? (value === null ? null : (selectedLabel ?? null));

  // Type-ahead. Filtering only while a query is being typed means arrowing into
  // an already-chosen field shows the whole list, which is what lets the user
  // change their mind without first clearing the box.
  const needle = query.trim().toLowerCase();
  const filtered =
    onQueryChange !== undefined || needle === ''
      ? options
      : options.filter((o) => o.label.toLowerCase().includes(needle));

  // Clamped rather than reset: the list shrinks under the cursor as the user
  // types, and an activedescendant pointing past the end names nothing at all.
  const active = filtered.length === 0 ? -1 : Math.min(Math.max(activeIndex, 0), filtered.length - 1);

  useEffect(() => {
    if (!open || active === -1) return;
    const node = listRef.current?.querySelector(`[data-index="${active}"]`);
    // jsdom implements no scrolling at all, so this is an optional call rather
    // than a guarded one.
    (node as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  function openList(index: number) {
    setOpen(true);
    setActiveIndex(index);
    onQueryChange?.('');
  }

  function closeList() {
    setOpen(false);
    setQuery('');
  }

  function commit(option: ComboboxOption) {
    onChange(option.value);
    closeList();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const selectedIndex = filtered.findIndex((option) => option.value === value);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          openList(selectedIndex === -1 ? 0 : selectedIndex);
        } else if (filtered.length > 0) {
          setActiveIndex((active + 1) % filtered.length);
        }
        return;

      case 'ArrowUp':
        event.preventDefault();
        if (!open) {
          openList(selectedIndex === -1 ? filtered.length - 1 : selectedIndex);
        } else if (filtered.length > 0) {
          setActiveIndex((active - 1 + filtered.length) % filtered.length);
        }
        return;

      case 'Home':
        if (!open) return;
        event.preventDefault();
        setActiveIndex(0);
        return;

      case 'End':
        if (!open) return;
        event.preventDefault();
        setActiveIndex(filtered.length - 1);
        return;

      case 'Enter': {
        if (!open) return;
        // preventDefault, not just "return": a combobox lives in a form, and
        // Enter on a text input submits it. Choosing an option must not save
        // the case.
        event.preventDefault();
        const option = filtered[active];
        if (option) commit(option);
        return;
      }

      case 'Escape':
        if (!open) return;
        event.preventDefault();
        // See the note at the top: the dialog around this must not also close.
        event.stopPropagation();
        closeList();
        return;

      case 'Tab':
        // No preventDefault — Tab must still move on. Closing here rather than
        // leaving it to blur keeps the popover from surviving a fast tab.
        if (open) closeList();
        return;

      default:
    }
  }

  return (
    <Field
      label={label}
      width={width}
      required={required}
      error={error}
      hint={hint}
      disabled={disabled}
      id={id}
      className={className}
    >
      {({ widthClassName, className: controlClassName, ...control }) => {
        const listboxId = `${control.id}-listbox`;
        const optionId = (index: number) => `${control.id}-option-${index}`;
        const activeOptionId = active === -1 ? undefined : optionId(active);

        return (
          <div className={[styles['combobox'], widthClassName].filter(Boolean).join(' ')}>
            <input
              {...control}
              ref={ref}
              type="text"
              role="combobox"
              autoComplete="off"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={open ? activeOptionId : undefined}
              // While the list is open the box holds the filter text, so the
              // current choice moves into the placeholder rather than vanishing.
              placeholder={open && selectedText !== null ? selectedText : placeholder}
              className={[controlClassName, styles['input']].filter(Boolean).join(' ')}
              value={open ? query : (selectedText ?? '')}
              onClick={() => {
                if (disabled) return;
                if (open) {
                  closeList();
                  return;
                }
                const selectedIndex = options.findIndex((option) => option.value === value);
                openList(selectedIndex === -1 ? 0 : selectedIndex);
              }}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpen(true);
                setActiveIndex(0);
                onQueryChange?.(event.target.value);
              }}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                closeList();
                onValidate?.(value);
              }}
            />

            <span className={styles['caret']} aria-hidden="true" data-open={open}>
              <CaretDownIcon size={14} />
            </span>

            {/*
              Rendered always, `hidden` when closed, so aria-controls always
              resolves to a real element. A dangling IDREF is not a smaller bug
              than a missing one — it just fails silently.
            */}
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={label}
              className={styles['listbox']}
              hidden={!open}
              aria-busy={loading || undefined}
            >
              {filtered.map((option, index) => (
                <li
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  data-index={index}
                  aria-selected={option.value === value}
                  className={[styles['option'], index === active ? styles['optionActive'] : null]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseDown={(event: MouseEvent<HTMLLIElement>) => {
                    // Keeps DOM focus on the input, which is the whole premise
                    // of the aria-activedescendant pattern. Without this the
                    // input blurs, closeList() runs, and the press lands on
                    // nothing.
                    event.preventDefault();
                  }}
                  // Commit on RELEASE, not on `onClick`. Two reasons, and the
                  // lint rule is the smaller one:
                  //  - It is what a native <select> popup does: press, drag
                  //    across the options, release on the one you want.
                  //  - `jsx-a11y/click-events-have-key-events` fires on an
                  //    `onClick` here and cannot be satisfied honestly. The
                  //    options in an aria-activedescendant listbox are not
                  //    focusable BY DESIGN — the keyboard contract lives on the
                  //    input that owns role="combobox" — so a key listener on
                  //    this <li> could never fire. Reported as a rule misfire in
                  //    task-21-report.md rather than suppressed.
                  onMouseUp={() => commit(option)}
                >
                  {option.label}
                  {option.value === value ? (
                    <span className={styles['check']} aria-hidden="true">
                      <CheckIcon size={13} />
                    </span>
                  ) : null}
                </li>
              ))}

              {filtered.length === 0 ? (
                // Not an <li>: a listbox may only contain options, and a
                // message that claimed role="option" would be selectable.
                <div className={styles['empty']}>
                  {loading && loadingMessage !== undefined ? loadingMessage : emptyMessage}
                </div>
              ) : null}
            </ul>
          </div>
        );
      }}
    </Field>
  );
});
